import { createContext, useContext, useRef, useState, useEffect } from 'react'
import Hls from 'hls.js'

const AudioCtx = createContext(null)

const HLS_URL = 'https://radio.stellarfm.fr/hls/stellarfm/live.m3u8'
const DIRECT_URL = 'https://radio.stellarfm.fr/listen/stellarfm/radio.mp3'
const API_URL = 'https://radio.stellarfm.fr/api/nowplaying/stellarfm'
const WS_URL = 'wss://radio.stellarfm.fr/api/live/nowplaying/websocket'
const STATION = 'stellarfm'

export function AudioProvider({ children }) {
  const audioRef = useRef(null)
  const hlsRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolumeState] = useState(0.8)
  const [isMuted, setIsMuted] = useState(false)

  // Now-playing state (partagé avec toute l'app)
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [streamLatency, setStreamLatency] = useState(0) // NOUVEAU: Latence réelle du flux
  const songIdRef = useRef(null)
  const lastFetchRef = useRef({ elapsed: 0, fetchedAt: null })

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.volume = volume
  }, [])

  // Destroy HLS instance on unmount
  useEffect(() => {
    return () => {
      hlsRef.current?.destroy()
    }
  }, [])

  const attachHls = (audio) => {
    if (Hls.isSupported()) {
      hlsRef.current?.destroy()
      const hls = new Hls({ lowLatencyMode: true })

      // Capture de la latence au changement de fragment
      hls.on(Hls.Events.FRAG_CHANGED, () => {
        if (hls.latency) {
          setStreamLatency(hls.latency)
        }
      })

      hls.loadSource(HLS_URL)
      hls.attachMedia(audio)
      hlsRef.current = hls
    } else if (audio.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari — HLS natif : très dur de mesurer la latence exacte sans Hls.js
      // On assume que le buffer natif a une latence d'environ 15s par défaut sur du 2s-segmentation
      audio.src = HLS_URL
      setStreamLatency(15)
    } else {
      // Fallback MP3 direct : latence très faible (~2/3secs)
      audio.src = DIRECT_URL
      setStreamLatency(3)
    }
  }

  const toggleRef = useRef(null)

  const toggle = async () => {
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) {
      audio.pause()
      hlsRef.current?.destroy()
      hlsRef.current = null
      audio.src = ''
      setIsPlaying(false)
    } else {
      try {
        attachHls(audio)
        audio.volume = volume
        audio.muted = isMuted
        await audio.play()
        setIsPlaying(true)
      } catch (e) {
        console.warn('Lecture impossible:', e)
      }
    }
  }

  const setVolume = (v) => {
    setVolumeState(v)
    if (audioRef.current) audioRef.current.volume = v
    if (v > 0) setIsMuted(false)
  }

  toggleRef.current = toggle

  const toggleMute = () => {
    if (!audioRef.current) return
    const next = !isMuted
    setIsMuted(next)
    audioRef.current.muted = next
  }

  // WebSocket — real-time now-playing (toujours actif, peu importe la page)
  useEffect(() => {
    let ws = null
    let reconnectTimeout = null
    let reconnectDelay = 2000
    let destroyed = false

    const applyNp = (np) => {
      if (!np) return
      setData(np)
      setError(false)
      const serverElapsed = np.now_playing?.elapsed || 0
      const isSameSong = np.now_playing?.sh_id === songIdRef.current
      lastFetchRef.current = { elapsed: serverElapsed, fetchedAt: Date.now() }
      if (!isSameSong) {
        songIdRef.current = np.now_playing?.sh_id
        setElapsed(serverElapsed)
      }
    }

    // Chargement REST immédiat avant que le WS soit établi
    fetch(API_URL).then(r => r.json()).then(applyNp).catch(() => setError(true))

    const connect = () => {
      if (destroyed) return
      ws = new WebSocket(WS_URL)

      ws.onopen = () => {
        reconnectDelay = 2000
        ws.send(JSON.stringify({ subs: { [`station:${STATION}`]: { recover: true } } }))
      }

      ws.onmessage = (event) => {
        try {
          const jsonData = JSON.parse(event.data)
          if ('connect' in jsonData) {
            const connectData = jsonData.connect
            if ('data' in connectData) {
              connectData.data.forEach(row => applyNp(row?.data?.np))
            } else {
              for (const sub of Object.values(connectData.subs ?? {})) {
                ; (sub.publications ?? []).forEach(row => applyNp(row?.data?.np))
              }
            }
          } else if ('pub' in jsonData) {
            applyNp(jsonData.pub?.data?.np)
          }
        } catch { }
      }

      ws.onerror = (e) => {
        // Prevent WS connection errors from showing up as Lighthouse errors
        e.preventDefault?.()
        setError(true)
      }

      ws.onclose = () => {
        if (destroyed) return
        reconnectTimeout = setTimeout(() => {
          reconnectDelay = Math.min(reconnectDelay * 2, 30000)
          connect()
        }, reconnectDelay)
      }
    }

    try { connect() } catch (e) { console.warn("WS setup failed", e) }

    return () => {
      destroyed = true
      clearTimeout(reconnectTimeout)
      if (ws) {
        ws.onopen = null
        ws.onmessage = null
        ws.onerror = null
        ws.onclose = null
        ws.close()
      }
    }
  }, [])

  // Incrémente elapsed localement entre les messages WS
  useEffect(() => {
    if (!isPlaying) return
    const { elapsed: fetchedElapsed, fetchedAt } = lastFetchRef.current
    if (fetchedAt !== null) {
      const synced = fetchedElapsed + Math.floor((Date.now() - fetchedAt) / 1000)
      setElapsed(synced)
    }
    const t = setInterval(() => setElapsed(e => e + 1), 1000)
    return () => clearInterval(t)
  }, [isPlaying, data?.now_playing?.sh_id])

  // Media Session — playback state
  useEffect(() => {
    if (!('mediaSession' in navigator)) return
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused'
  }, [isPlaying])

  // Media Session — action handlers (registered once via ref)
  useEffect(() => {
    if (!('mediaSession' in navigator)) return
    navigator.mediaSession.setActionHandler('play', () => toggleRef.current?.())
    navigator.mediaSession.setActionHandler('pause', () => toggleRef.current?.())
    // Suppress skip/seek controls on iOS lock screen (live radio, no seeking)
    try { navigator.mediaSession.setActionHandler('seekforward', () => { }) } catch { }
    try { navigator.mediaSession.setActionHandler('seekbackward', () => { }) } catch { }
    try { navigator.mediaSession.setActionHandler('previoustrack', () => { }) } catch { }
    try { navigator.mediaSession.setActionHandler('nexttrack', () => { }) } catch { }
    return () => {
      try {
        navigator.mediaSession.setActionHandler('play', null)
        navigator.mediaSession.setActionHandler('pause', null)
        navigator.mediaSession.setActionHandler('seekforward', null)
        navigator.mediaSession.setActionHandler('seekbackward', null)
        navigator.mediaSession.setActionHandler('previoustrack', null)
        navigator.mediaSession.setActionHandler('nexttrack', null)
      } catch { }
    }
  }, [])

  // Media Session — metadata (pochette, titre, artiste) — actif sur toutes les pages
  useEffect(() => {
    if (!('mediaSession' in navigator)) return
    const s = data?.now_playing?.song
    navigator.mediaSession.metadata = new MediaMetadata({
      title: s?.title || 'StellarFM',
      artist: s?.artist || 'Le son de maintenant',
      album: s?.album || 'StellarFM',
      artwork: s?.art ? [
        { src: s.art, sizes: '256x256', type: 'image/jpeg' },
        { src: s.art, sizes: '512x512', type: 'image/jpeg' },
      ] : [],
    })
  }, [data?.now_playing?.song?.title, data?.now_playing?.song?.artist])

  // Media Session — barre de progression
  const duration = data?.now_playing?.duration || 0
  const displayElapsed = duration ? Math.min(elapsed, duration) : elapsed
  useEffect(() => {
    if (!('mediaSession' in navigator) || !duration || !isPlaying) return
    try {
      navigator.mediaSession.setPositionState({
        duration,
        playbackRate: 1,
        position: Math.min(displayElapsed, duration),
      })
    } catch { }
  }, [displayElapsed, duration, isPlaying])

  return (
    <AudioCtx.Provider value={{
      isPlaying, toggle, volume, setVolume, isMuted, toggleMute,
      nowPlaying: data, elapsed, npError: error, streamLatency
    }}>
      <audio ref={audioRef} preload="none" />
      {children}
    </AudioCtx.Provider>
  )
}

export const useAudio = () => useContext(AudioCtx)
